import type { ObjectField } from './fields'
import type { Draft } from './draft'
import type { VenueMarketingContent } from '@lib/dto/venue-marketing.dto'
import {
  VENUE_MARKETING_SECTION_LABELS,
  type VenueMarketingSectionKey,
} from '@lib/constants/venue-marketing-sections'

/**
 * The descriptors are checked against the contract they describe.
 *
 * Every key below is a key of the actual document type, so a misspelling does
 * not compile. That is worth the type machinery because of how the mistake
 * behaved without it: the save schema is strict only at the top level, so a
 * wrong key *inside* a section was silently stripped — somebody typed a value,
 * saved, and it vanished with no complaint anywhere. Neither the compiler nor
 * the tests caught `startlable` for `startlabel`.
 */
type SectionKey = keyof VenueMarketingContent
type SectionValue<K extends SectionKey> = NonNullable<VenueMarketingContent[K]>

/** What a list of `T` holds, or never if `T` is not a list. */
type ListElement<T> = T extends readonly (infer E)[] ? E : never

/**
 * Keys of `S` holding text — the `text` and `textarea` editors.
 *
 * `& string` throughout: `keyof` also admits numbers and symbols, and a field
 * key is written into a path that becomes a string.
 */
type StringKeys<S> = {
  [K in keyof S]-?: NonNullable<S[K]> extends string ? K : never
}[keyof S] &
  string

/**
 * Keys of `S` holding a list of plain strings.
 *
 * Tested against the array type rather than against its element: `ListElement`
 * of a non-list is `never`, and `never extends string` is *true*, so asking
 * about the element let every plain string field pass as a list of them.
 */
type StringListKeys<S> = {
  [K in keyof S]-?: NonNullable<S[K]> extends readonly string[] ? K : never
}[keyof S] &
  string

/** Keys of `S` holding a list of objects — cards, rows, packages. */
type ObjectListKeys<S> = {
  [K in keyof S]-?: NonNullable<S[K]> extends readonly string[]
    ? never
    : NonNullable<S[K]> extends readonly object[]
      ? K
      : never
}[keyof S] &
  string

/** Keys of `S` holding a nested object, such as `micro.tasting`. */
type GroupKeys<S> = {
  [K in keyof S]-?: NonNullable<S[K]> extends readonly string[] | readonly object[]
    ? never
    : NonNullable<S[K]> extends object
      ? K
      : never
}[keyof S] &
  string

/** One named field inside a repeating object, checked against that object. */
interface ObjectFieldOf<E> {
  key: StringKeys<E>
  label: string
  multiline?: boolean
  image?: boolean
}

/** A field descriptor whose key must exist on the section it describes. */
type FieldSpecOf<S> =
  | { kind: 'text'; key: StringKeys<S>; label: string; optional?: boolean; hint?: string; placeholder?: string }
  | { kind: 'image'; key: StringKeys<S>; label: string; optional?: boolean; hint?: string }
  | { kind: 'textarea'; key: StringKeys<S>; label: string; optional?: boolean; hint?: string; rows?: number }
  | {
      kind: 'strings'
      key: StringListKeys<S>
      label: string
      itemLabel: string
      hint?: string
      placeholder?: string
    }
  | {
      kind: 'objects'
      key: ObjectListKeys<S>
      label: string
      itemLabel: string
      fields: readonly ObjectFieldOf<ListElement<NonNullable<S[ObjectListKeys<S> & keyof S]>>>[]
      hint?: string
    }
  | {
      kind: 'group'
      key: GroupKeys<S>
      label: string
      fields: readonly FieldSpecOf<NonNullable<S[GroupKeys<S> & keyof S]>>[]
    }

/**
 * One editable field, as the renderer sees it — with the section forgotten.
 *
 * The renderer walks descriptors from every section at once and cannot be
 * generic over each, so keys widen to `string` here. They were already checked
 * where the descriptor was written, by `section()`.
 *
 * `optional` matters more than it looks: emptying a required field stores an
 * empty string (which the schema allows), while emptying an optional one
 * removes the key. The save schema is strict, and an optional field present but
 * blank is not the same as absent — `bookingPrivate: ''` renders a dead button
 * where leaving it out shows "coming soon".
 */
export type FieldSpec =
  | { kind: 'text'; key: string; label: string; optional?: boolean; hint?: string; placeholder?: string }
  | { kind: 'image'; key: string; label: string; optional?: boolean; hint?: string }
  | { kind: 'textarea'; key: string; label: string; optional?: boolean; hint?: string; rows?: number }
  | { kind: 'strings'; key: string; label: string; itemLabel: string; hint?: string; placeholder?: string }
  | {
      kind: 'objects'
      key: string
      label: string
      itemLabel: string
      fields: readonly ObjectField[]
      hint?: string
    }
  | { kind: 'group'; key: string; label: string; fields: readonly FieldSpec[] }

export interface SectionSpec {
  /** Checked: a key of the document, not any string. */
  key: SectionKey
  label: string
  /** What this section is, in the words somebody filling it in would use. */
  description: string
  /**
   * Whether the page works without it.
   *
   * Presence is the on switch — there is no `enabled` flag anywhere in the
   * contract, so removing the section is how a venue turns it off.
   */
  optional: boolean
  fields: readonly FieldSpec[]
  /** Sections whose editing is not a list of fields. */
  custom?: 'pricing'
}

/**
 * Declare one section, checking every key against the contract.
 *
 * The argument is checked against `SectionValue<K>`; the result is the widened
 * `SectionSpec` the renderer consumes. So a typo is a compile error at the
 * descriptor, and nothing downstream has to be generic.
 *
 * The label is not given here: it comes from `VENUE_MARKETING_SECTION_LABELS`,
 * which also names sections in the record of what an assistant changed.
 */
function section<K extends VenueMarketingSectionKey>(spec: {
  key: K
  description: string
  optional: boolean
  fields: readonly FieldSpecOf<SectionValue<K>>[]
  custom?: 'pricing'
}): SectionSpec {
  return { ...spec, label: VENUE_MARKETING_SECTION_LABELS[spec.key] }
}

const IMAGE_HINT = 'A full image address. Leave empty for none — the page falls back to text.'

export const MARKETING_SECTIONS: readonly SectionSpec[] = [
  section({
    key: 'hero',
    description:
      'The top of the page. Use one heading style: a wordmark image, the three-part wording, or three stacked lines — two of them set is a page with two headings.',
    optional: false,
    fields: [
      { kind: 'image', key: 'image', label: 'Background image', hint: IMAGE_HINT },
      { kind: 'textarea', key: 'sub', label: 'Subheading' },
      { kind: 'image', key: 'lettering', label: 'Wordmark image', optional: true, hint: IMAGE_HINT },
      { kind: 'text', key: 'headPre', label: 'Heading — before', optional: true },
      { kind: 'text', key: 'headEm', label: 'Heading — emphasized', optional: true },
      { kind: 'text', key: 'headPost', label: 'Heading — after', optional: true },
      { kind: 'text', key: 'line1', label: 'Stacked line 1', optional: true },
      { kind: 'text', key: 'line2', label: 'Stacked line 2', optional: true },
      { kind: 'text', key: 'line3', label: 'Stacked line 3', optional: true },
    ],
  }),
  section({
    key: 'intro',
    description: 'The opening pitch, with the small pill that sits above it.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body', rows: 4 },
      {
        kind: 'text',
        key: 'tag',
        label: 'Pill',
        hint: 'The small line above the fold, e.g. "★ 2026–27 Pricing · $350 Admin Fee".',
      },
    ],
  }),
  section({
    key: 'guide',
    description: 'Who the couple will be dealing with.',
    optional: false,
    fields: [
      { kind: 'text', key: 'name', label: 'Name' },
      { kind: 'text', key: 'role', label: 'Role' },
      { kind: 'textarea', key: 'bio', label: 'Biography', rows: 4 },
      { kind: 'text', key: 'sign', label: 'Sign-off' },
      { kind: 'text', key: 'btn', label: 'Button text', optional: true },
      { kind: 'image', key: 'photo', label: 'Photograph', optional: true, hint: IMAGE_HINT },
      {
        kind: 'text',
        key: 'initial',
        label: 'Monogram',
        optional: true,
        hint: 'Shown when there is no photograph. Up to four characters.',
      },
    ],
  }),
  section({
    key: 'snapshot',
    description: 'The at-a-glance seasons and their starting prices.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'textarea', key: 'range', label: 'Closing sentence' },
      {
        kind: 'objects',
        key: 'cards',
        label: 'Cards',
        itemLabel: 'card',
        fields: [
          { key: 'season', label: 'Season' },
          { key: 'months', label: 'Months' },
          { key: 'startlabel', label: 'Prefix' },
          { key: 'price', label: 'Price' },
        ],
      },
    ],
  }),
  section({
    key: 'included',
    description: 'Everything that comes with the venue.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'strings', key: 'items', label: 'Items', itemLabel: 'item' },
      { kind: 'image', key: 'photo', label: 'Photograph', optional: true, hint: IMAGE_HINT },
      { kind: 'text', key: 'caption', label: 'Caption', optional: true },
    ],
  }),
  section({
    key: 'pricing',
    description: 'The two price tables. The column headers decide how wide each row is.',
    optional: false,
    custom: 'pricing',
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'textarea', key: 'foot', label: 'Footnote' },
    ],
  }),
  section({
    key: 'addons',
    description: 'Extras with their prices.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body', hint: 'May be left empty.' },
      {
        kind: 'objects',
        key: 'rows',
        label: 'Add-ons',
        itemLabel: 'add-on',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'sub', label: 'Detail' },
          { key: 'price', label: 'Price' },
        ],
      },
    ],
  }),
  section({
    key: 'spend',
    description: 'What couples usually spend, as tiers rather than a price list.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'textarea', key: 'note', label: 'Note' },
      {
        kind: 'objects',
        key: 'cards',
        label: 'Tiers',
        itemLabel: 'tier',
        fields: [
          { key: 't', label: 'Tier' },
          { key: 'g', label: 'Guests' },
          { key: 'r', label: 'Range' },
          { key: 'd', label: 'Description', multiline: true },
        ],
      },
    ],
  }),
  section({
    key: 'micro',
    description: 'Smaller packages, and the tasting block beneath them.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'image', key: 'image', label: 'Section image', optional: true, hint: IMAGE_HINT },
      {
        kind: 'objects',
        key: 'cards',
        label: 'Packages',
        itemLabel: 'package',
        fields: [
          { key: 'title', label: 'Title' },
          { key: 'price', label: 'Price' },
          { key: 'meta', label: 'Detail' },
          { key: 'desc', label: 'Description', multiline: true },
          { key: 'image', label: 'Image', image: true },
        ],
      },
      {
        kind: 'group',
        key: 'tasting',
        label: 'Tasting',
        fields: [
          { kind: 'text', key: 'heading', label: 'Heading' },
          { kind: 'textarea', key: 'body', label: 'Body' },
          { kind: 'image', key: 'image', label: 'Image', optional: true, hint: IMAGE_HINT },
          {
            kind: 'objects',
            key: 'meta',
            label: 'Details',
            itemLabel: 'detail',
            fields: [
              { key: 'l', label: 'Label' },
              { key: 'v', label: 'Value' },
            ],
          },
        ],
      },
    ],
  }),
  section({
    key: 'terms',
    description: 'The fine print.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'note', label: 'Note' },
      {
        kind: 'objects',
        key: 'items',
        label: 'Terms',
        itemLabel: 'term',
        fields: [
          { key: 'l', label: 'Label' },
          { key: 'body', label: 'Text', multiline: true },
        ],
      },
    ],
  }),
  section({
    key: 'contact',
    description: 'How to reach the venue.',
    optional: false,
    fields: [
      { kind: 'text', key: 'address', label: 'Address' },
      { kind: 'text', key: 'phone', label: 'Phone, as displayed', placeholder: '256.202.6622' },
      { kind: 'text', key: 'phoneHref', label: 'Phone, digits only', placeholder: '2562026622' },
      { kind: 'text', key: 'email', label: 'Email' },
      { kind: 'text', key: 'location', label: 'City and state' },
      {
        kind: 'text',
        key: 'bookedEmail',
        label: 'Email for booked couples',
        optional: true,
        hint: 'Shown only to guests who have already booked.',
      },
    ],
  }),
  section({
    key: 'cta',
    description: 'The closing block and its booking links.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'image', key: 'image', label: 'Image', hint: IMAGE_HINT },
      { kind: 'text', key: 'btnPrimary', label: 'Primary button' },
      { kind: 'text', key: 'btnSecondary', label: 'Secondary button' },
      {
        kind: 'text',
        key: 'bookingWedding',
        label: 'Wedding booking link',
        hint: 'A web address (https://…) or a link on this page, such as #tour.',
      },
      {
        kind: 'text',
        key: 'bookingPrivate',
        label: 'Private event booking link',
        optional: true,
        hint: 'Left empty, the private-event option shows as coming soon.',
      },
    ],
  }),
  section({
    key: 'footer',
    description: 'The bottom line of the page.',
    optional: false,
    fields: [
      { kind: 'text', key: 'managed', label: 'Managed by' },
      { kind: 'text', key: 'copyright', label: 'Copyright' },
    ],
  }),
  section({
    key: 'brand',
    description:
      'The marks shown in the page’s top bar and footer. Leave one empty and the site shows text instead.',
    optional: false,
    fields: [
      {
        kind: 'image',
        key: 'logo',
        label: 'Venue logo (for light backgrounds)',
        optional: true,
        hint: 'Shown in the page’s top bar. Dark artwork on transparency — this is the one that sits on white.',
      },
      {
        kind: 'image',
        key: 'logoOnDark',
        label: 'Venue logo (for dark backgrounds)',
        optional: true,
        hint: 'Shown in the footer. Usually the same mark as a white knockout. Left empty, the page uses the one above.',
      },
      {
        kind: 'image',
        key: 'cseLogo',
        label: 'Common Sense Events logo',
        optional: true,
        hint: 'Shown beside the venue’s. The same image on every venue.',
      },
      {
        kind: 'text',
        key: 'cseUrl',
        label: 'Link',
        optional: true,
        hint: 'A web address (https://…) or a link on this page. Left empty, the mark is not a link.',
      },
    ],
  }),
  section({
    key: 'grapeband',
    description: 'The full-width band across the page.',
    optional: false,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'image', key: 'image', label: 'Image', hint: IMAGE_HINT },
    ],
  }),

  // ── Optional. Adding the section is what turns it on. ──────────────────────
  section({
    key: 'premium',
    description: 'Larger packages with their timelines and capacities.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      {
        kind: 'objects',
        key: 'packages',
        label: 'Packages',
        itemLabel: 'package',
        fields: [
          { key: 'title', label: 'Title' },
          { key: 'price', label: 'Price' },
          { key: 'timeline', label: 'Timeline' },
          { key: 'capacity', label: 'Capacity' },
          { key: 'lodging', label: 'Lodging', multiline: true },
        ],
      },
    ],
  }),
  section({
    key: 'privateEvents',
    description: 'Non-wedding bookings.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'text', key: 'price', label: 'Price' },
      { kind: 'strings', key: 'items', label: 'What’s included', itemLabel: 'item' },
      {
        kind: 'objects',
        key: 'addons',
        label: 'Add-ons',
        itemLabel: 'add-on',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'price', label: 'Price' },
        ],
      },
    ],
  }),
  section({
    key: 'buyout',
    description: 'Taking the whole property.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'strings', key: 'items', label: 'What’s included', itemLabel: 'item' },
    ],
  }),
  section({
    key: 'community',
    description: 'The venue’s local involvement, with an optional link.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'text', key: 'linkText', label: 'Link text' },
      {
        kind: 'text',
        key: 'linkUrl',
        label: 'Link address',
        hint: 'A web address (https://…) or a link on this page. Left empty, the text is shown without a link.',
      },
    ],
  }),
  section({
    key: 'outdoor',
    description: 'What there is to do on the property.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      {
        kind: 'objects',
        key: 'activities',
        label: 'Activities',
        itemLabel: 'activity',
        fields: [
          { key: 'title', label: 'Title' },
          { key: 'price', label: 'Price' },
          { key: 'duration', label: 'Duration' },
          { key: 'details', label: 'Details', multiline: true },
        ],
      },
    ],
  }),
  section({
    key: 'packages',
    description: 'All-inclusive wedding packages, priced per person.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      {
        kind: 'objects',
        key: 'tiers',
        label: 'Packages',
        itemLabel: 'package',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'perperson', label: 'Per person' },
          { key: 'desc', label: 'Description', multiline: true },
        ],
      },
    ],
  }),
  section({
    key: 'barpackages',
    description: 'Drinks packages, minimums and perks.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'textarea', key: 'minimums', label: 'Minimums' },
      { kind: 'textarea', key: 'perks', label: 'Perks' },
      {
        kind: 'objects',
        key: 'packages',
        label: 'Packages',
        itemLabel: 'package',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'price', label: 'Price' },
          { key: 'hours', label: 'Hours' },
          { key: 'extra', label: 'Extra' },
          { key: 'desc', label: 'Description', multiline: true },
        ],
      },
    ],
  }),
  section({
    key: 'lodging',
    description:
      'One on-site option with a weekday and weekend rate. A page has this or Overnight stays, never both.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      { kind: 'text', key: 'weekday', label: 'Weekday rate' },
      { kind: 'text', key: 'weekend', label: 'Weekend rate' },
    ],
  }),
  section({
    key: 'overnight',
    description:
      'Several accommodation types with price ranges. A page has this or Lodging, never both.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      {
        kind: 'objects',
        key: 'rates',
        label: 'Rates',
        itemLabel: 'rate',
        fields: [
          { key: 'type', label: 'Type' },
          { key: 'range', label: 'Range' },
        ],
      },
    ],
  }),
  section({
    key: 'spaces',
    description: 'The rooms and areas, described for the public page.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      {
        kind: 'objects',
        key: 'list',
        label: 'Spaces',
        itemLabel: 'space',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'desc', label: 'Description', multiline: true },
        ],
      },
    ],
  }),
  section({
    key: 'alacarte',
    description: 'Individually priced extras.',
    optional: true,
    fields: [
      { kind: 'text', key: 'heading', label: 'Heading' },
      { kind: 'textarea', key: 'body', label: 'Body' },
      {
        kind: 'objects',
        key: 'options',
        label: 'Options',
        itemLabel: 'option',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'price', label: 'Price' },
          { key: 'includes', label: 'Includes', multiline: true },
        ],
      },
    ],
  }),
]

/**
 * A blank section, so adding one gives its fields something to be keys on.
 *
 * Required fields get an empty string, which every text rule in the contract
 * allows; optional ones are left out, because presence is meaningful and an
 * optional field present-but-blank is not the same as absent. Lists start empty.
 */
export function blankSection(spec: SectionSpec): Draft {
  const blank: Draft = {}
  for (const field of spec.fields) {
    if (field.kind === 'strings' || field.kind === 'objects') blank[field.key] = []
    else if (field.kind === 'group') blank[field.key] = {}
    else if (field.optional !== true) blank[field.key] = ''
  }
  return blank
}
